import type { MainBindings } from '../../../../native/main/types/bindings';
import { injectFactory, TypedContainer } from '@thinkalpha/common/ioc/TypedContainer.js';
import { injectable } from 'inversify';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { BehaviorSubject, from, skip } from 'rxjs';
import type { ReactContainer } from 'src/ReactContainer';
import type { PerWidgetBindings, ReactBindings } from 'src/types/bindings';

const iocContext = createContext<ReactContainer>(new TypedContainer<ReactBindings>());

export const useContainer = () => useContext(iocContext);

export const ContainerProvider = iocContext.Provider;

export const inject = injectFactory<ReactBindings & MainBindings & PerWidgetBindings>();

/**
 * Set this property to `true` on a service to prevent ReactiveInjectable from
 * auto-capturing it into a consumer's disposableStack. Use this on singleton
 * services whose lifetime is managed by the DI container, not by individual
 * widget models.
 *
 * @deprecated Temporary until ReactiveInjectable is removed.
 */
export const skipAutoDispose: unique symbol = Symbol('skipAutoDispose');

/** @deprecated -- Just use a bare class and don't use @reacts */
@injectable()
export class ReactiveInjectable implements Disposable {
    protected disposableStack = new DisposableStack();

    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private __abortController = new AbortController();

    __observable = new BehaviorSubject(this);

    constructor(...args: any[]) {
        if (Reflect.hasMetadata(REACTS_PARAMETERS_METADATA, this.constructor)) {
            for (const parameterIndex of Reflect.getMetadata(REACTS_PARAMETERS_METADATA, this.constructor)) {
                const value = args[parameterIndex];

                if (value === undefined) {
                    throw new Error('Must use `arguments` on `super()` when using @reacts');
                }

                // if (!(value instanceof ReactiveInjectable)) {
                // if (!('__observable' in value)) {
                //     throw new Error(
                //         'Cannot use `@reacts` with constructor parameter which is not a `ReactiveInjectable`.',
                //     );
                // }

                const subscription = from(value)
                    .pipe(skip(1))
                    .subscribe((_) => {
                        if (this.__abortController.signal.aborted) {
                            return;
                        }
                        this.__observable.next(this);
                    });

                this.disposableStack.use({
                    [Symbol.dispose]: () => {
                        subscription.unsubscribe();
                    },
                });
            }
        }

        for (const arg of args) {
            if (typeof arg !== 'object' || arg === null) continue;
            if (skipAutoDispose in arg && arg[skipAutoDispose]) continue;
            if (Symbol.dispose in arg) {
                this.disposableStack.use(arg);
            }
        }
    }

    [Symbol.observable]() {
        return this.__observable;
    }

    /**
     * `Symbol.observable` is undefined in our runtime for some reason, so we use this as a fallback.
     */
    ['@@observable']() {
        return this.__observable;
    }

    rerender() {
        if (this.__abortController.signal.aborted) {
            return;
        }
        this.__observable.next(this);
    }

    [Symbol.dispose]() {
        this.__abortController.abort();
        this.disposableStack.dispose();
    }
}

const REACTS_PARAMETERS_METADATA = 'REACTS_PARAMETERS_METADATA';

export function reacts(target: any, propertyKey?: string, descriptorOrIndex?: number | PropertyDescriptor) {
    if (descriptorOrIndex === undefined) {
        throw new Error('Must use `@reacts` on either an accessor or constructor parameter.');
    }

    if (typeof descriptorOrIndex === 'number') {
        let reactsParameters: number[] = [];
        if (Reflect.hasOwnMetadata(REACTS_PARAMETERS_METADATA, target)) {
            reactsParameters = Reflect.getMetadata(REACTS_PARAMETERS_METADATA, target);
        }

        reactsParameters.push(descriptorOrIndex);

        Reflect.defineMetadata(REACTS_PARAMETERS_METADATA, reactsParameters, target);
    } else {
        const originalSet = descriptorOrIndex.set!;
        descriptorOrIndex.set = function (value) {
            originalSet.call(this, value);

            if (((this as any).__abortController as AbortController).signal.aborted) {
                return;
            }
            (this as any).__observable.next(this);
        };
    }
}

export const useReactive = <T extends ReactiveInjectable>(service: T): [T, number] => {
    const [v, setRender] = useState(0);

    useEffect(() => {
        const abortController = new AbortController();

        const subscription = from(service)
            .pipe(skip(1))
            .subscribe(() => {
                if (abortController.signal.aborted) {
                    return;
                }

                setRender((i) => i + 1);
            });
        return () => {
            abortController.abort();
            subscription.unsubscribe();
        };
    }, [service]);

    // Ensure value doesn't change unless the service does so this can be fed to context
    const value = useMemo<[T, number]>(() => [service, v], [service, v]);

    return value;
};

export { injectable };
